import React, { useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  TextField,
  Typography,
} from '@mui/material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'

import { MaskedNumber } from '@/components/UI/MaskedNumber'

import {
  ALL_VALUES,
  EMPTY_FILTERS,
  FILTER_FIELDS,
  FilterField,
  hasActiveFilters,
  IFieldSelection,
  isAllValues,
  isAmountRangeInvalid,
  ITransactionFilters,
  letsThrough,
} from './filterModel'

interface PillProps {
  label: string
  options: string[]
  selection: IFieldSelection
  onChange: (selection: IFieldSelection) => void
}

// One field's control: a pill naming the set, opening the values present in the rows read so far.
// The whole set shows every value ticked -- unticking one leaves it out. Show all toggles between
// every value and a blank slate to pick a few from.
const FilterPill: React.FC<PillProps> = ({ label, options, selection, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  // A blank slate stays in the menu until a value is picked: a filter matching nothing could never
  // return a row, so the table keeps showing the whole set meanwhile.
  const [isBlank, setIsBlank] = useState(false)
  const isOpen = Boolean(anchor)
  const showingAll = isAllValues(selection)
  const isTicked = (option: string) => !isBlank && letsThrough(selection, option)
  const allTicked = showingAll && !isBlank
  const tickedCount = options.filter(isTicked).length

  // From a blank slate a pick keeps only that value. Otherwise the value flips within the current
  // selection, so unticking from the whole set leaves it out and later values still come through.
  const toggle = (option: string) => {
    if (isBlank) {
      setIsBlank(false)
      onChange({ mode: 'only', values: [option] })
      return
    }

    const values = selection.values.includes(option)
      ? selection.values.filter(value => value !== option)
      : [...selection.values, option]
    const next: IFieldSelection = { mode: selection.mode, values }

    // Unticking the last value of a pick leaves a blank slate rather than a pick of nothing. An
    // exclusion is kept even with no known value left ticked: the options are only those read so
    // far, and values on pages not yet read still come through.
    if (next.mode === 'only' && next.values.length === 0) {
      setIsBlank(true)
      onChange(ALL_VALUES)
      return
    }
    // Kept as picked, even with every known value ticked: the options are only those read so far,
    // so picking them all is not the same as the whole set. Re-ticking the last value left out
    // empties the exclusions, which is the whole set again.
    onChange(next)
  }

  // From every value ticked, Show all clears to a blank slate; from anything else, it ticks every
  // value. Either way the table shows the whole set.
  const toggleAll = () => {
    setIsBlank(allTicked)
    onChange(ALL_VALUES)
  }

  // A blank slate is only a starting point for picking, so closing on one goes back to every value.
  const close = () => {
    setAnchor(null)
    setIsBlank(false)
  }

  return (
    <>
      <Pill
        disableRipple
        disabled={options.length === 0}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        endIcon={isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        onClick={event => setAnchor(event.currentTarget)}
        className={showingAll ? undefined : 'is-active'}
      >
        {showingAll ? `All ${label.toLowerCase()}` : label}
        {!showingAll && <Count>{tickedCount}</Count>}
      </Pill>

      <Menu
        anchorEl={anchor}
        open={isOpen}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '10px', minWidth: 220, mt: 0.5 } } }}
      >
        {/* A dash rather than a tick while only some values are picked. */}
        <ValueItem onClick={toggleAll}>
          <Checkbox size="small" disableRipple checked={allTicked} indeterminate={!showingAll} />
          <ListItemText primary="Show all" />
        </ValueItem>

        {options.map(option => (
          <ValueItem
            key={option}
            // Highlighted only once narrowed, or every row would light up on the whole set.
            selected={!showingAll && isTicked(option)}
            onClick={() => toggle(option)}
          >
            <Checkbox size="small" disableRipple checked={isTicked(option)} />
            {/* Account numbers arrive masked; drawn as the table draws them so the asterisks sit level. */}
            <ListItemText primary={<MaskedNumber value={option} />} />
          </ValueItem>
        ))}
      </Menu>
    </>
  )
}

interface FiltersProps {
  filters: ITransactionFilters
  options: Record<FilterField, string[]>
  onChange: (filters: ITransactionFilters) => void
}

const RANGE_ERROR_ID = 'transaction-amount-range-error'

export const TransactionFilters: React.FC<FiltersProps> = ({ filters, options, onChange }) => {
  const rangeInvalid = isAmountRangeInvalid(filters)

  return (
    <FilterBar>
      {FILTER_FIELDS.map(field => (
        <FilterPill
          key={field.key}
          label={field.label}
          options={options[field.key]}
          selection={filters.selections[field.key]}
          onChange={selection =>
            onChange({ ...filters, selections: { ...filters.selections, [field.key]: selection } })
          }
        />
      ))}

      {/* Inclusive bounds, usable alone or together as a range. */}
      <AmountField
        size="small"
        type="number"
        label="Amount at least"
        value={filters.minAmount}
        error={rangeInvalid}
        inputProps={{ 'aria-describedby': rangeInvalid ? RANGE_ERROR_ID : undefined }}
        onChange={event => onChange({ ...filters, minAmount: event.target.value })}
      />

      <AmountField
        size="small"
        type="number"
        label="Amount at most"
        value={filters.maxAmount}
        error={rangeInvalid}
        inputProps={{ 'aria-describedby': rangeInvalid ? RANGE_ERROR_ID : undefined }}
        onChange={event => onChange({ ...filters, maxAmount: event.target.value })}
      />

      {/* Beside the fields rather than beneath them, so the bar does not grow taller. */}
      {rangeInvalid && (
        <RangeError id={RANGE_ERROR_ID} role="alert">
          &ldquo;Amount at least&rdquo; is more than &ldquo;Amount at most&rdquo;, so the amount
          filter is not applied.
        </RangeError>
      )}

      {hasActiveFilters(filters) && (
        <Button size="small" onClick={() => onChange(EMPTY_FILTERS)}>
          Clear filters
        </Button>
      )}
    </FilterBar>
  )
}

const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  paddingBottom: theme.spacing(2),
}))

const Pill = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  border: '1px solid #E4E7EC',
  borderRadius: '999px',
  padding: '5px 12px',
  backgroundColor: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#F9FAFB',
    borderColor: '#D0D5DD',
  },
  '&.is-active': {
    borderColor: theme.palette.primary.main,
  },
}))

const Count = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(0.75),
  minWidth: 20,
  padding: '0 6px',
  borderRadius: '999px',
  backgroundColor: '#F2F4F7',
  fontSize: '12px',
  lineHeight: '20px',
  textAlign: 'center',
}))

const ValueItem = styled(MenuItem)({
  paddingLeft: 4,
  fontSize: '14px',
})

const RangeError = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: theme.palette.error.main,
  maxWidth: 360,
}))

const AmountField = styled(TextField)({
  width: 180,
  '& .MuiOutlinedInput-root': {
    borderRadius: '999px',
  },
})
